import { createContext, useContext, useEffect, useState } from "react";
import useAllowPartner from "../../auth/accessToken/hooks/useAllowPartner";
import { useAccountId } from "../../auth/useAccountId";
import { useNoticeContext } from "../../common/components/NoticeBanner/FullContext";
import useMediaQuery from "../../common/hooks/useMediaQuery";
import usePaymentsEnabled from "../../common/hooks/usePaymentsEnabled";
import { globalMobileBreakpoint } from "../../styles/constants";

type EnvPatcherProps = {
    children: React.ReactNode;
};

type PendingTestMode = {
    // The mode just toggled to, while the app still shows the previous
    // account until the new one has loaded
    pendingIsTest: boolean | undefined;
    setPendingIsTest: (isTest: boolean) => void;
};

const PendingTestModeContext = createContext<PendingTestMode>({
    pendingIsTest: undefined,
    setPendingIsTest: () => {},
});

// For the top bar, so the test mode toggle, colours and badge change on click
export const usePendingTestMode = () => useContext(PendingTestModeContext);

export default function EnvPatcher({ children }: EnvPatcherProps) {
    const accountId = useAccountId();
    const isInspecting = useAllowPartner();
    const [pendingIsTest, setPendingIsTest] = useState<boolean>();
    // biome-ignore lint/correctness/useExhaustiveDependencies: Reset when the account has switched
    useEffect(() => {
        setPendingIsTest(undefined);
    }, [accountId]);
    // The toggled mode right away, so the mobile top bar grows or shrinks
    // with the test mode strip on click instead of once the account loads
    const isTest = pendingIsTest ?? accountId.startsWith("T");
    const isPaymentsEnabled = usePaymentsEnabled();
    const isMobile = useMediaQuery(`(max-width: ${globalMobileBreakpoint}px)`);
    const notice = useNoticeContext();
    const isNoticeActive = notice.active;

    return (
        <PendingTestModeContext.Provider
            value={{ pendingIsTest, setPendingIsTest }}
        >
            <div
                style={{
                    display: "contents",
                    ...(isMobile &&
                        (isInspecting || isTest || !isPaymentsEnabled) && {
                            // double the size of the topbar
                            "--topbar-height":
                                "var(--topbar-mobile-indicator-height)",
                        }),
                    ...(isNoticeActive && {
                        "--topbar-height":
                            "var(--topbar-mobile-indicator-height)",
                        ...(isMobile &&
                            (isInspecting || isTest || !isPaymentsEnabled) && {
                                "--topbar-height":
                                    "var(--topbar-2x-mobile-indicator-height)",
                            }),
                    }),
                }}
            >
                {children}
            </div>
        </PendingTestModeContext.Provider>
    );
}
